if (!customElements.get('product-interactions')) {
  customElements.define('product-interactions', class extends HTMLElement {
    connectedCallback() {
      if (this.root) return;
      this.root = this.closest('product-info');
      if (!this.root) return;
      this.connectionId = (this.connectionId || 0) + 1;
      this.copyStates = new Map();
      this.copyLabels = new Map();
      this.dialogOpeners = new Map();
      this.onClick = this.handleClick.bind(this);
      this.onClose = (event) => {
        const opener = this.dialogOpeners.get(event.target);
        if (opener && opener.isConnected) opener.focus();
        this.dialogOpeners.delete(event.target);
      };
      this.onOutsideClick = (event) => {
        this.root.querySelectorAll('[data-pdp-share][open]').forEach(share => {
          if (!share.contains(event.target)) share.open = false;
        });
      };
      this.root.addEventListener('click', this.onClick, true);
      this.root.addEventListener('close', this.onClose, true);
      document.addEventListener('click', this.onOutsideClick);

      const gallery = this.root.querySelector('media-gallery');
      if (gallery) {
        this.galleryObserver = new MutationObserver(() => this.updateGalleryButtons());
        this.galleryObserver.observe(gallery, { childList: true, subtree: true, attributes: true, attributeFilter: ['aria-current'] });
        this.updateGalleryButtons();
      }
      if (this.hasAttribute('data-ask-status')) {
        const button = this.root.querySelector('[data-pdp-ask-open]');
        const dialog = this.root.querySelector('[id^="pdp-ask-dialog-"]');
        if (button && dialog) {
          this.openDialog(dialog, button);
          const status = dialog.querySelector('[data-pdp-ask-status]');
          if (status) status.focus();
        }
      }
    }

    disconnectedCallback() {
      if (!this.root) return;
      this.connectionId++;
      this.root.removeEventListener('click', this.onClick, true);
      this.root.removeEventListener('close', this.onClose, true);
      document.removeEventListener('click', this.onOutsideClick);
      if (this.galleryObserver) this.galleryObserver.disconnect();
      this.copyStates.forEach((state, button) => {
        clearTimeout(state.timer);
        if (state.label) state.label.textContent = state.original;
        button.removeAttribute('aria-disabled');
      });
      this.copyStates.clear();
      this.copyLabels.forEach((original, label) => { label.textContent = original; });
      this.copyLabels.clear();
      this.dialogOpeners.clear();
      this.root = null;
    }

    openDialog(dialog, opener) {
      if (dialog.open) return;
      this.dialogOpeners.set(dialog, opener);
      if (typeof dialog.showModal === 'function') dialog.showModal();
      else dialog.setAttribute('open', '');
    }

    closeDialog(dialog) {
      if (typeof dialog.close === 'function') dialog.close();
      else {
        dialog.removeAttribute('open');
        this.onClose({ target: dialog });
      }
    }

    updateGalleryButtons() {
      const gallery = this.root.querySelector('media-gallery');
      if (!gallery || !gallery.querySelector('.thumbnail-list .thumbnail')) return;
      const slider = gallery.querySelector(':scope > slider-component:first-of-type');
      if (slider) slider.querySelectorAll('.slider-button--prev, .slider-button--next').forEach(button => { button.disabled = false; });
    }

    handleClick(event) {
      if (!(event.target instanceof Element) || event.target.closest('product-info') !== this.root) return;
      const target = event.target;
      const arrow = target.closest('.slider-button--prev, .slider-button--next');
      const gallery = arrow && arrow.closest('media-gallery');
      if (gallery && arrow.closest('slider-component') === gallery.querySelector(':scope > slider-component:first-of-type')) {
        const thumbs = [...gallery.querySelectorAll('.thumbnail-list .thumbnail')];
        if (thumbs.length) {
          event.preventDefault();
          event.stopPropagation();
          const active = Math.max(0, thumbs.findIndex(thumb => thumb.getAttribute('aria-current') === 'true'));
          const direction = arrow.classList.contains('slider-button--next') ? 1 : -1;
          thumbs[(active + direction + thumbs.length) % thumbs.length].click();
          return;
        }
      }
      const ask = target.closest('[data-pdp-ask-open]');
      if (ask) {
        const dialog = this.root.querySelector('[id^="pdp-ask-dialog-"]');
        if (dialog) this.openDialog(dialog, ask);
        return;
      }
      const shipping = target.closest('.product__tax a[href*="/policies/shipping-policy"]');
      if (shipping && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey) {
        const dialog = this.root.querySelector('[id^="pdp-shipping-dialog-"]');
        if (dialog) {
          event.preventDefault();
          this.openDialog(dialog, shipping);
        }
        return;
      }
      const dialog = target.closest('dialog.pdp-dialog');
      if (dialog) {
        const rect = dialog.getBoundingClientRect();
        const outside = target === dialog && (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom);
        const fallbackClose = typeof dialog.close !== 'function' && target.closest('form[method="dialog"] button');
        if (outside || fallbackClose) {
          event.preventDefault();
          this.closeDialog(dialog);
        }
      }
      const copy = target.closest('[data-pdp-copy]');
      if (copy) {
        event.preventDefault();
        if (!this.copyStates.has(copy)) this.copyLink(copy);
      }
    }

    async copyLink(button) {
      const connectionId = this.connectionId;
      const label = button.querySelector('[data-pdp-copy-label]');
      if (label && !this.copyLabels.has(label)) this.copyLabels.set(label, label.textContent);
      const state = { label, original: label ? this.copyLabels.get(label) : '' };
      this.copyStates.set(button, state);
      button.setAttribute('aria-disabled', 'true');
      if (label) label.setAttribute('aria-live', 'polite');
      let success = false;
      try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(button.dataset.pdpCopy);
          success = true;
        } else {
          const textarea = document.createElement('textarea');
          textarea.value = button.dataset.pdpCopy;
          textarea.style.position = 'fixed';
          textarea.style.opacity = '0';
          document.body.appendChild(textarea);
          textarea.select();
          try { success = document.execCommand('copy'); }
          finally { textarea.remove(); button.focus(); }
        }
      } catch (error) {
        success = false;
      }
      if (!this.isConnected || connectionId !== this.connectionId) return;
      if (label) label.textContent = success ? this.dataset.copySuccess : this.dataset.copyError;
      if (!success) {
        button.removeAttribute('aria-disabled');
        this.copyStates.delete(button);
        return;
      }
      state.timer = setTimeout(() => {
        if (label) label.textContent = state.original;
        button.removeAttribute('aria-disabled');
        this.copyStates.delete(button);
      }, 1800);
    }
  });
}
